import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Activity, TrendingUp, TrendingDown, RefreshCw, Clock,
  CheckCircle, XCircle, Zap, Globe, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ── Mini sparkline barre verticali ───────────────────────────────────────────
function MiniBar({ value, max, color = '#c9a84c', height = '32px' }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ height, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={{
        width: '100%', background: color, opacity: 0.7,
        height: `${Math.max(3, pct)}%`, borderRadius: '1px',
        transition: 'height 0.3s',
      }} />
    </div>
  );
}

// ── Heatmap oraria (0-23 ore) ─────────────────────────────────────────────
function HourlyHeatmap({ data }) {
  if (!data || data.length !== 24) return null;
  const max = Math.max(...data, 1);
  const groups = [
    { label: 'Notte', hours: [0, 1, 2, 3, 4, 5] },
    { label: 'Mattina', hours: [6, 7, 8, 9, 10, 11] },
    { label: 'Pomeriggio', hours: [12, 13, 14, 15, 16, 17] },
    { label: 'Sera', hours: [18, 19, 20, 21, 22, 23] },
  ];

  const intensity = (v) => {
    if (v === 0) return 'rgba(201,168,76,0.04)';
    const pct = v / max;
    if (pct < 0.25) return 'rgba(201,168,76,0.15)';
    if (pct < 0.5) return 'rgba(201,168,76,0.35)';
    if (pct < 0.75) return 'rgba(201,168,76,0.6)';
    return 'rgba(201,168,76,0.9)';
  };

  return (
    <div>
      {groups.map(g => (
        <div key={g.label} className="mb-3">
          <p className="text-[10px] mb-1.5" style={{ color: 'var(--lux-text-muted-3)', minWidth: 72 }}>{g.label}</p>
          <div className="flex gap-1">
            {g.hours.map(h => (
              <div key={h} className="flex-1 relative" title={`${h}:00 — ${data[h]} eventi`}>
                <div className="w-full rounded-sm"
                  style={{ height: '24px', background: intensity(data[h]), transition: 'background 0.3s' }} />
                <p className="text-center mt-0.5" style={{ fontSize: '9px', color: 'var(--lux-text-muted-3)' }}>{h}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3 mt-2">
        <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>Intensità:</p>
        {[0.04, 0.15, 0.35, 0.6, 0.9].map((o, i) => (
          <div key={i} className="flex items-center gap-1">
            <div className="w-4 h-3 rounded-sm" style={{ background: `rgba(201,168,76,${o})` }} />
            <p style={{ fontSize: '9px', color: 'var(--lux-text-muted-3)' }}>
              {['0', 'basso', 'medio', 'alto', 'picco'][i]}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Trend 7 giorni ────────────────────────────────────────────────────────
function TrendChart({ success, failure }) {
  if (!success || success.length === 0) return null;
  const max = Math.max(...success.map((v, i) => v + (failure[i] || 0)), 1);
  const days = ['L-6', 'L-5', 'L-4', 'L-3', 'L-2', 'Ieri', 'Oggi'];

  return (
    <div>
      <div className="flex items-end gap-1 h-16">
        {success.map((s, i) => {
          const f = failure[i] || 0;
          const total = s + f;
          const sH = total > 0 ? `${Math.round((s / max) * 100)}%` : '3px';
          const fH = total > 0 ? `${Math.round((f / max) * 100)}%` : '0';
          return (
            <div key={i} className="flex-1 flex flex-col justify-end gap-0.5" title={`${days[i]}: ${s} ok / ${f} fail`}>
              {f > 0 && <div className="w-full rounded-sm" style={{ height: fH, background: 'rgba(239,68,68,0.5)', minHeight: '2px' }} />}
              <div className="w-full rounded-sm" style={{ height: sH, background: 'rgba(16,185,129,0.6)', minHeight: '2px' }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1">
        {days.map(d => <span key={d} style={{ fontSize: '9px', color: 'var(--lux-text-muted-3)', flex: 1, textAlign: 'center' }}>{d}</span>)}
      </div>
      <div className="flex gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm" style={{ background: 'rgba(16,185,129,0.6)' }} />
          <span style={{ fontSize: '10px', color: 'var(--lux-text-muted-3)' }}>Successo</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm" style={{ background: 'rgba(239,68,68,0.5)' }} />
          <span style={{ fontSize: '10px', color: 'var(--lux-text-muted-3)' }}>Fallimento</span>
        </div>
      </div>
    </div>
  );
}

export function WebhookAnalytics({ token, tenantSlug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}`, 'X-Tenant-Slug': tenantSlug };

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/webhooks/analytics`, { headers });
      setData(d);
    } catch (e) {
      toast.error('Errore caricamento analytics webhook.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="text-center py-20" style={{ color: 'var(--lux-text-muted)' }}>
      <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto mb-2"
        style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
      <p className="text-xs">Analisi delivery webhook...</p>
    </div>
  );

  if (!data || data.total_deliveries === 0) return (
    <div className="text-center py-20" style={{ color: 'var(--lux-text-muted)' }}>
      <Activity size={36} className="mx-auto mb-4 opacity-20" />
      <p className="text-sm mb-1">Nessun evento webhook negli ultimi 7 giorni.</p>
      <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
        Configura un webhook e attiva il test per vedere le analytics.
      </p>
    </div>
  );

  const eventColors = {
    'booking.created': '#6366f1', 'booking.confirmed': '#10b981',
    'booking.cancelled': '#ef4444', 'booking.checkin': '#f59e0b',
    'access.command': '#a78bfa', 'guest_key.generated': '#06b6d4',
    'webhook.test': '#6b7280',
  };

  return (
    <div data-testid="webhook-analytics">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-2xl font-light" style={{ color: 'var(--lux-text)' }}>Webhook Analytics</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
            Ultimi 7 giorni · {data.total_deliveries} consegne totali
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded"
          style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
          <RefreshCw size={13} /> Aggiorna
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: 'Tasso Successo', value: `${data.success_rate}%`,
            sub: `${data.success_count} ok / ${data.failure_count} fail`,
            icon: data.success_rate >= 90 ? TrendingUp : TrendingDown,
            color: data.success_rate >= 90 ? '#10b981' : '#ef4444',
          },
          {
            label: 'Latenza Media', value: `${data.avg_latency_ms}ms`,
            sub: `P95: ${data.p95_latency_ms}ms`,
            icon: Clock, color: data.avg_latency_ms < 500 ? '#10b981' : '#f59e0b',
          },
          {
            label: 'Consegne OK', value: data.success_count,
            sub: `${data.total_deliveries} totali`, icon: CheckCircle, color: '#10b981',
          },
          {
            label: 'Consegne Fail', value: data.failure_count,
            sub: data.failure_count === 0 ? 'Perfetto!' : 'Controlla i log',
            icon: XCircle, color: data.failure_count === 0 ? '#10b981' : '#ef4444',
          },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="p-4 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={13} style={{ color }} />
              <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>{label}</p>
            </div>
            <p className="font-serif text-2xl font-light mb-0.5" style={{ color: 'var(--lux-text)' }}>{value}</p>
            <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Trend 7gg */}
        <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Trend Consegne — 7 giorni</p>
          <TrendChart success={data.daily_trend?.success} failure={data.daily_trend?.failure} />
        </div>

        {/* Per webhook */}
        <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Per Endpoint</p>
          {(data.per_webhook || []).length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>Nessun dato.</p>
          ) : (
            <div className="space-y-4">
              {data.per_webhook.map(wh => (
                <div key={wh.webhook_id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate" style={{ color: 'var(--lux-text-sec)' }}>{wh.label}</p>
                      <p className="text-[10px] truncate font-mono" style={{ color: 'var(--lux-text-muted-3)' }}>{wh.url}</p>
                    </div>
                    <div className="flex items-center gap-3 ml-3 shrink-0 text-xs">
                      <span style={{ color: wh.success_rate >= 90 ? '#10b981' : '#ef4444' }}>{wh.success_rate}%</span>
                      <span style={{ color: 'var(--lux-text-muted-3)' }}>{wh.avg_latency_ms}ms</span>
                    </div>
                  </div>
                  <div className="h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div className="h-1 rounded-full"
                      style={{ width: `${wh.success_rate}%`, background: wh.success_rate >= 90 ? '#10b981' : '#ef4444' }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Heatmap oraria */}
      <div className="p-5 rounded-sm mb-6" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <p className="text-xs uppercase tracking-widest mb-5" style={{ color: 'var(--lux-gold)' }}>Heatmap Oraria (UTC)</p>
        <HourlyHeatmap data={data.hourly_heatmap} />
      </div>

      {/* Event distribution */}
      {Object.keys(data.event_distribution || {}).length > 0 && (
        <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Distribuzione per Tipo Evento</p>
          <div className="space-y-3">
            {Object.entries(data.event_distribution)
              .sort(([, a], [, b]) => b - a)
              .map(([event, count]) => {
                const color = eventColors[event] || '#6b7280';
                const pct = data.total_deliveries > 0 ? Math.round(count / data.total_deliveries * 100) : 0;
                return (
                  <div key={event}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                        <code className="text-xs" style={{ color: 'var(--lux-text-sec)' }}>{event}</code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color }}>{count}</span>
                        <span className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{pct}%</span>
                      </div>
                    </div>
                    <div className="h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="h-1 rounded-full" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
